<template>
    <div class="">
        <div class="flex flex-col gap-4">
            <h2 class="text-2xl font-bold text-blue">
                Send your requirements directly to {{ agency.Name }}
            </h2>
            <span class="text-base text-grey-70">
                This will be delivered via email to their
                <span class="font-bold">priority</span>
                email account
            </span>
            <HorizontalSeparator class="my-4" />
        </div>

        <h2 class="text-sm md:text-3xl font-bold text-blue mb-5">Job Details:</h2>

        <div class="md:hidden field-title">
            Job Title
        </div>
        <div class="flex mb-4">
            <div class="hidden md:flex input-group-addon">
                Job Title
            </div>
            <InputText class="rounded-l md:rounded-l-none rounded-r text-grey-90" name="jobTitle"
                placeholder="Enter job title" :value="jobTitle" :error="errors.hasOwnProperty('formFields.jobTitle')"
                :error-message="getErrorMessage(errors, 'formFields.jobTitle')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'jobTitle',
                            value: event.target.value,
                        })
                    " />
        </div>

        <div class="md:hidden field-title">
            Job Location
        </div>
        <div class="flex mb-4">
            <div class="hidden md:flex input-group-addon">
                Job Location
            </div>
            <InputText class="rounded-l md:rounded-l-none rounded-r text-grey-90" name="jobLocation"
                placeholder="Enter job location" :value="jobLocation"
                :error="errors.hasOwnProperty('formFields.jobLocation')"
                :error-message="getErrorMessage(errors, 'formFields.jobLocation')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'jobLocation',
                            value: event.target.value,
                        })
                    " />
        </div>

        <div class="md:hidden field-title">
            Employment Type
        </div>
        <div class="mb-4">
            <InputDropdown name="employmentType" placeholder="Select employment type" :items="employmentTypeOptions"
                :value="employmentType" :class="{
                    'bg-red-200': 'formFields.employmentType' in errors,
                }" @change="(value) =>
                        $emit('changeParameter', {
                            key: 'employmentType',
                            value,
                        })
                    " />
        </div>

        <span v-if="'formFields.employmentType' in errors" class="-mt-2 text-sm text-red-600">{{ getErrorMessage(errors,
                    'formFields.employmentType') }}</span>

        <label class="text-grey-70">
            Enquiry
            <InputTextArea name="enquiry" rows="5" placeholder="Enquiry" :value="enquiry"
                :error="errors.hasOwnProperty('formFields.enquiry')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'enquiry',
                            value: event.target.value,
                        })
                    " />
        </label>
        <span v-if="'formFields.enquiry' in errors" class="-mt-2 text-sm text-red-600">{{ getErrorMessage(errors,
                    'formFields.enquiry') }}</span>

        <HorizontalSeparator class="my-8" />
        <h2 class="text-sm md:text-3xl font-bold text-blue mb-5">Your Details:</h2>

        <div class="md:hidden field-title">
            Email Address
        </div>
        <div class="flex mb-4">
            <div
                class="hidden md:flex input-group-addon">
                Email Address
            </div>
            <InputText class="rounded-l md:rounded-l-none rounded-r text-grey-90" name="emailAddress"
                placeholder="Your email address" :value="emailAddress"
                :error="errors.hasOwnProperty('formFields.emailAddress')"
                :error-message="getErrorMessage(errors, 'formFields.emailAddress')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'emailAddress',
                            value: event.target.value,
                        })
                    " />
        </div>

        <div class="md:hidden field-title">
            Company Name
        </div>
        <div class="flex mb-4">
            <div
                class="hidden md:flex input-group-addon">
                Company Name
            </div>
            <InputText class="rounded-l md:rounded-l-none rounded-r text-grey-90" name="companyName"
                placeholder="Your company name" :value="companyName"
                :error="errors.hasOwnProperty('formFields.companyName')"
                :error-message="getErrorMessage(errors, 'formFields.companyName')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'companyName',
                            value: event.target.value,
                        })
                    " />
        </div>

        <div class="md:hidden field-title">
            Contact Name
        </div>
        <div class="flex mb-4">
            <div
                class="hidden md:flex input-group-addon">
                Contact Name
            </div>
            <InputText class="rounded-l md:rounded-l-none rounded-r text-grey-90" name="contactName"
                placeholder="Your contact name" :value="contactName"
                :error="errors.hasOwnProperty('formFields.contactName')"
                :error-message="getErrorMessage(errors, 'formFields.contactName')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'contactName',
                            value: event.target.value,
                        })
                    " />
        </div>

        <div class="md:hidden field-title">
            Phone Number
        </div>
        <div class="flex mb-4">
            <div
                class="hidden md:flex input-group-addon">
                Phone Number
            </div>
            <InputText class="rounded-l md:rounded-l-none rounded-r text-grey-90" name="telephoneNumber"
                placeholder="Your phone number" :value="telephoneNumber"
                :error="errors.hasOwnProperty('formFields.telephoneNumber')"
                :error-message="getErrorMessage(errors, 'formFields.telephoneNumber')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'telephoneNumber',
                            value: event.target.value,
                        })
                    " />
        </div>

        <HorizontalSeparator class="my-8" />

        <h2 class="text-sm md:text-3xl font-bold text-blue mb-5">Attach a Job Spec (Optional):</h2>
        <div class="mb-4">
            <InputFile name="jobSpecification" :value="jobSpecification"
                :error="errors.hasOwnProperty('formFields.jobSpecification')"
                :error-message="getErrorMessage(errors, 'formFields.jobSpecification')" @change="(event) =>
                        $emit('changeParameter', {
                            key: 'jobSpecification',
                            value: event.target.files[0],
                        })
        " />
        </div>

        <div class="rounded border border-grey-25 bg-grey-5 p-2 drop-shadow mb-4">
            <h2 class="text-2xl font-bold text-blue mb-3">Options:</h2>
            <label class="text-lg text-grey-70">
                <input type="checkbox" name="receiveConfirmation" checked />
                Receive confirmation the email has been sent.
            </label>
        </div>

        <div class="flex w-full flex-col gap-4 lg:w-3/12">
            <InputButton style-type="contact-form" @click="submitForm">
                Send email
            </InputButton>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import HorizontalSeparator from 'Components/HorizontalSeparator';
import InputButton from 'Components/InputButton';
import InputText from 'Components/InputText';
import InputDropdown from 'Components/InputDropdown';
import InputTextArea from 'Components/InputTextArea';
import { getErrorMessage } from 'Helpers/helpers';
import InputFile from 'Components/InputFile';

export default {
    name: 'ContactEmployerEmailForm',
    components: {
        InputButton,
        InputFile,
        InputTextArea,
        InputDropdown,
        InputText,
        HorizontalSeparator,
    },
    data() {
        return {
            employmentTypeOptions: {
                permanent: 'Permanent',
                temp: 'Temp',
                contract: 'Contract',
                'temp-to-perm': 'Temp to Perm',
            },
        };
    },
    props: {
        agency: {
            type: Object,
        },
        errors: {
            type: Object,
            default() {
                return {};
            },
        },
        jobTitle: {
            type: String,
        },
        jobLocation: {
            type: String,
        },
        employmentType: {
            type: String,
        },
        enquiry: {
            type: String,
        },
        emailAddress: {
            type: String,
        },
        companyName: {
            type: String,
        },
        contactName: {
            type: String,
        },
        telephoneNumber: {
            type: String,
        },
        jobSpecification: {
            type: String,
        },
    },
    computed: {
        ...mapState('contact', ['success']),
    },
    methods: {
        ...mapActions('contact', ['loadPersistentInformation', 'registerContact', ]),
    ...mapMutations('contact', ['setIsSkipped']),
        getErrorMessage,
        submitForm() {
            this.registerContact()
                .then((response) => {
                    console.log('Form Submitted Successfully', response);
                // reset the skipped flag so that if we visit a tel/fax/website etc then
                // the next register sorts it.
                this.setIsSkipped(false);
                    this.$emit('submitForm');
                })
                .catch((error) => {
                    console.error('Error submitting form', error);
                })
                ;
        }
    },
    mounted() {
        this.loadPersistentInformation('EmailEmployerEnquiry');
        this.$emit('changeParameter', {
            key: 'user-enquiry-type',
            value: 'enquiry',
        });
    },
};
</script>
